import { motion } from "framer-motion";
import { Facebook, Twitter, Linkedin, Instagram, Mail, MapPin, Phone } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    services: [
      { name: "IUL Insurance", href: "#services" },
      { name: "Fixed Indexed Annuity", href: "#services" },
      { name: "Term Life Insurance", href: "#services" },
      { name: "Simplified Issue Life", href: "#services" },
    ],
    company: [
      { name: "About Us", href: "#about" },
      { name: "Our Partners", href: "#partners" },
      { name: "Careers", href: "#recruitment" },
      { name: "Contact", href: "#contact" },
    ],
    resources: [
      { name: "Financial Planning", href: "#" },
      { name: "Insurance Guide", href: "#" },
      { name: "Retirement Calculator", href: "#" },
      { name: "FAQs", href: "#" },
    ],
  };

  const socialLinks = [
    { icon: Facebook, href: "#", label: "Facebook" },
    { icon: Twitter, href: "#", label: "Twitter" },
    { icon: Linkedin, href: "#", label: "LinkedIn" },
    { icon: Instagram, href: "#", label: "Instagram" },
  ];

  const scrollToSection = (href) => {
    if (href.startsWith("#")) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <footer className="bg-navy text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-6"
            >
              <div className="mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_insurance-guide-8/artifacts/nhiwlosf_image.png" 
                  alt="WealthConnexs"
                  className="h-16 w-auto"
                />
              </div>
              <p className="text-slate-400 leading-relaxed max-w-sm">
                Helping people build their financial futures through comprehensive
                insurance solutions and expert guidance.
              </p>

              {/* Contact Info */}
              <div className="mt-4 space-y-2">
                <a href="tel:+16463022901" className="flex items-center gap-2 text-slate-400 hover:text-amber-500 transition-colors">
                  <Phone className="w-4 h-4" />
                  <span className="text-sm">646-302-2901</span>
                </a>
                <a href="mailto:info@wealthconnexs.com" className="flex items-center gap-2 text-slate-400 hover:text-amber-500 transition-colors">
                  <Mail className="w-4 h-4" />
                  <span className="text-sm">info@wealthconnexs.com</span>
                </a>
              </div>
            </motion.div>

            {/* Canada Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6">
              <MapPin className="w-4 h-4 text-amber-500" />
              <span className="text-sm">Available in Canada & USA</span>
            </div>

            {/* Social Links */}
            <div className="flex gap-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  aria-label={social.label}
                  className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-amber-500 transition-colors duration-200"
                  data-testid={`social-${social.label.toLowerCase()}`}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="font-semibold mb-4 text-white">Services</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-slate-400 hover:text-amber-500 transition-colors text-sm footer-link"
                    data-testid={`footer-link-service-${index}`}
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-semibold mb-4 text-white">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-slate-400 hover:text-amber-500 transition-colors text-sm footer-link"
                    data-testid={`footer-link-company-${index}`}
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <h4 className="font-semibold mb-4 text-white">Resources</h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-slate-400 hover:text-amber-500 transition-colors text-sm footer-link"
                    data-testid={`footer-link-resource-${index}`}
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-slate-400">
              © {currentYear} WealthConnexs. All rights reserved.
            </div>
            <div className="flex gap-6 text-sm text-slate-400">
              <a href="#" className="hover:text-amber-500 transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="hover:text-amber-500 transition-colors">
                Terms of Service
              </a>
              <a href="#" className="hover:text-amber-500 transition-colors">
                Disclosures
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
